'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { X, Trash2, ClipboardList, ArrowRight } from 'lucide-react';
import { clsx } from 'clsx';
import { ArticleNameBlock, ProductImage } from '@paeffgen/react/crm-documents/ui';
import {
  useBodyScrollLock,
  usePrefersReducedMotion,
} from '@paeffgen/react/crm-documents/util';
import { useCart } from './cart-provider';

// Swipe-to-close: ab dieser Strecke nach rechts oder dieser Geschwindigkeit
// (Pixel pro Millisekunde) wird das Sheet geschlossen.
const SWIPE_CLOSE_DISTANCE_PX = 120;
const SWIPE_CLOSE_VELOCITY = 0.5;
// iOS-Sheet-Curve — fühlt sich am nativsten an (gleiche wie BottomSheet).
const SHEET_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const SHEET_DURATION_MS = 280;

export function CartSidebar() {
  const {
    items,
    isOpen,
    toggleCart,
    closeCart,
    removeItem,
    itemCount,
    canMutate,
  } = useCart();
  const pathname = usePathname();
  const isOnMaterialanfrage = pathname === '/materialanfrage';

  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ x: number; time: number } | null>(null);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [mounted, setMounted] = useState(isOpen);
  const [entered, setEntered] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const animMs = prefersReducedMotion ? 0 : SHEET_DURATION_MS;

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      return;
    }
    setEntered(false);
    const t = window.setTimeout(() => setMounted(false), animMs);
    return () => window.clearTimeout(t);
  }, [isOpen, animMs]);

  // Enter-Animation erst NACH dem Commit von mounted=true triggern.
  // Wichtig: dependency auf `mounted` (nicht nur `isOpen`) — sonst rennen
  // die rAFs schon, bevor React den Initial-Render mit translateX(100%)
  // committet hat. Ergebnis sonst: beide State-Updates landen im selben
  // Render und der Drawer springt ohne Animation auf translateX(0).
  useEffect(() => {
    if (!isOpen || !mounted) return;
    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);
    };
  }, [isOpen, mounted]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeCart();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeCart]);

  useBodyScrollLock(mounted);

  useEffect(() => {
    if (!isOpen) {
      setDragX(0);
      setIsDragging(false);
      dragStartRef.current = null;
    }
  }, [isOpen]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Drag-Close ist eine Touch-Geste — Maus/Stift ignorieren.
    if (e.pointerType !== 'touch') return;
    dragStartRef.current = { x: e.clientX, time: Date.now() };
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return;
    const delta = e.clientX - dragStartRef.current.x;
    // Nur nach rechts (Drawer schließen) — leichter Widerstand nach links
    // (rubber-band-Effekt).
    setDragX(delta > 0 ? delta : delta / 4);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return;
    const delta = e.clientX - dragStartRef.current.x;
    const duration = Math.max(1, Date.now() - dragStartRef.current.time);
    const velocity = delta / duration;
    dragStartRef.current = null;
    setIsDragging(false);
    if (delta > SWIPE_CLOSE_DISTANCE_PX || velocity > SWIPE_CLOSE_VELOCITY) {
      closeCart();
    } else {
      setDragX(0);
    }
  };

  const sheetTransform =
    dragX > 0
      ? `translateX(${dragX}px)`
      : entered
        ? 'translateX(0)'
        : 'translateX(100%)';

  return (
    <>
      {mounted && (
        <div
          className="fixed inset-0 bg-black/50 z-[80]"
          onClick={closeCart}
          aria-hidden="true"
          style={{
            opacity: entered ? 1 : 0,
            transition: `opacity ${animMs}ms ease-out`,
            pointerEvents: entered ? 'auto' : 'none',
          }}
        />
      )}

      {mounted && (
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          aria-label="Anfrage Quickmenü"
          className="fixed inset-y-0 right-0 z-[90] w-[calc(100vw-3rem)] max-w-md md:w-full md:max-w-md bg-white shadow-2xl flex flex-col"
          style={{
            transform: sheetTransform,
            transition: isDragging
              ? 'none'
              : `transform ${animMs}ms ${SHEET_EASING}`,
            touchAction: isDragging ? 'none' : undefined,
            willChange: 'transform',
          }}
        >
          {/* Grab-Strich: links zentriert, nur mobil */}
          <div
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            role="button"
            tabIndex={-1}
            aria-label="Zum Schließen nach rechts ziehen"
            className="md:hidden absolute left-0 top-1/2 -translate-y-1/2 flex items-center justify-center px-3 py-6 cursor-grab active:cursor-grabbing touch-none select-none z-10"
          >
            <span className="w-1 h-10 rounded-full bg-zinc-300" />
          </div>

          <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-primary text-white">
            <div className="flex items-center gap-2">
              <ClipboardList size={20} />
              <h2 className="font-bold text-lg">
                Anfrage ({itemCount})
              </h2>
            </div>
            <button
              onClick={toggleCart}
              className="p-1 hover:bg-white/20 rounded transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {items.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <ClipboardList size={48} className="mb-4 opacity-20" />
                <p>Ihre Anfrage ist leer.</p>
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100 relative group"
                >
                  <ProductImage
                    src={item.imageUrl}
                    alt={item.description || item.articleNumber}
                    size="md"
                    className="bg-white border-gray-200"
                  />

                  <div className="flex-1 min-w-0">
                    <ArticleNameBlock
                      className="pr-6"
                      articleNumber={item.articleNumber}
                      description={item.description || item.articleNumber}
                      description2={item.description2}
                      descriptionClassName="font-medium text-sm text-gray-900"
                      description2ClassName="text-xs text-gray-600"
                      articleNumberClassName="text-xs text-gray-500 font-mono"
                      singleLineTitle
                    />
                  </div>

                  <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => removeItem(item.articleNumber)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Entfernen"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {items.length > 0 && (
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <Link
                href="/materialanfrage"
                onClick={closeCart}
                className="w-full py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary-hover shadow-lg shadow-orange-200 transition-all flex items-center justify-center gap-2"
              >
                Anfrage bearbeiten <ArrowRight size={18} />
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Floating Toggle Button (visible only when items in cart, closed, single customer context, and not already on /materialanfrage) */}
      {!isOpen && itemCount > 0 && canMutate && !isOnMaterialanfrage && (
        <button
          onClick={toggleCart}
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] md:bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-6 z-40 bg-primary text-white p-4 rounded-full shadow-xl hover:bg-primary-hover hover:scale-105 transition-all animate-in slide-in-from-bottom-4 flex items-center justify-center"
        >
          <div className="relative">
            <ClipboardList size={24} />
            <span className="absolute -top-3 -right-3 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-white">
              {itemCount}
            </span>
          </div>
        </button>
      )}
    </>
  );
}
